import { HTMLAttributes } from 'react'
import { useRouter } from 'next/router'

import Container from '@/components/base/Container'
import Cta from '@/components/base/Cta'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { projectsBlock } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData } from '@/types'

export type ProjectsBlockProps = HTMLAttributes<HTMLElement> &
  Partial<{
    button: WPLink
    paragraph: string
    poster: WPImage
    title: string
    title_component: TypographyProps['component']
    theme: WPPageData['acf']['theme']
  }>

const ProjectsBlock = ({
  button,
  paragraph,
  poster,
  title,
  title_component,
  theme,
  ...props
}: ProjectsBlockProps) => {
  const router = useRouter()

  const handleSectionClick = () => {
    if (button?.url) router.push(button.url, button.url, { scroll: false })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (button?.url && e.key === 'Enter')
      router.push(button.url, button.url, { scroll: false })
  }
  return (
    <Motion animation={projectsBlock}>
      {(motionRef) => (
        <section
          ref={motionRef}
          {...props}
          className={cn([
            {
              'bg-dark-100 text-dark-500': theme === 'light',
              'bg-dark-600 text-white': theme === 'dark',
              'cursor-pointer': button?.url
            }
          ])}
          onClick={button?.url ? handleSectionClick : undefined}
          onKeyDown={button?.url ? handleKeyDown : undefined}
          role={button?.url ? 'link' : undefined}
          tabIndex={button?.url ? 0 : undefined}
        >
          <Container>
            <Grid>
              <div className="col-span-4 md:col-span-6 lg:col-span-12">
                <div
                  className={cn([
                    'aspect-h-4 aspect-w-3 relative z-10 overflow-hidden lg:aspect-h-9 lg:aspect-w-16',
                    'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/30'
                  ])}
                >
                  {poster ? (
                    <>
                      <Image
                        fill
                        source={poster}
                        fit="cover"
                        data-motion-image
                      />
                    </>
                  ) : null}
                  <div className="absolute z-20 flex flex-col items-center justify-center text-white">
                    {title ? (
                      <Typography
                        component={title_component}
                        variant="h3"
                        className="mb-[0.8rem] !font-normal"
                        data-motion-title
                      >
                        {title}
                      </Typography>
                    ) : null}
                    {paragraph ? (
                      <Typography
                        variant="h1"
                        className="mb-[1.6rem] text-center font-serif italic"
                        data-motion-paragraph
                      >
                        {paragraph}
                      </Typography>
                    ) : null}
                    {button ? (
                      <Cta link={button} theme="light" data-motion-cta />
                    ) : null}
                  </div>
                </div>
              </div>
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default ProjectsBlock
